import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { deepClone } from '@finra/forms';
import {
  AbstractRedlineService,
  DataCollectionService,
  findField,
  getDeepestRouteSnapshot,
  NavigationData,
  OverlayService,
} from '@finra/rampdi-forms-common';
import { catchError, take } from 'rxjs/operators';
import { EMPTY } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class RedlineService extends AbstractRedlineService {
  private currentTitle: string = 'View All Changes';

  constructor(
    public route: ActivatedRoute,
    private DCS: DataCollectionService,
    private OS: OverlayService,
  ) {
    super(route, DCS, OS);
  }

  /**
   * Extends parent class functionality to set dynamic titles for the redline widget
   */
  setTitle(title: string) {
    this.currentTitle = title;
    this.refreshRedlineDataWithTitle();
  }

  /**
   * Refreshes redline data with current title if redline is open
   */
  private refreshRedlineDataWithTitle() {
    this.redlineDiff$
      .pipe(
        take(1),
        catchError((error) => {
          console.error('Error refreshing redline data with title:', error);
          return EMPTY;
        }),
      )
      .subscribe((currentData) => {
        if (currentData && currentData.open) {
          const updatedData = { ...currentData, title: this.currentTitle };
          this.setRedlineData(updatedData);
        }
      });
  }

  preprocessNavData(navData: NavigationData[]) {
    // Include all navigation items, not just children of the last item
    // This ensures sections that are skipped with subsection children are properly linked
    const allNavItems = [];

    function flattenNavData(items: NavigationData[]) {
      items.forEach((item) => {
        if (!item.skip) {
          allNavItems.push(item);
        }
        if (item.children) {
          // If parent is skipped, prefix children with parent name
          if (item.skip) {
            item.children.forEach((child) => {
              if (!child.skip) {
                allNavItems.push({
                  ...child,
                  text: item.type === 'header' ? child.text : `${item.text} - ${child.text}`,
                });
              } else {
                flattenNavData(item.children);
              }
            });
          } else {
            flattenNavData(item.children);
          }
        }
      });
    }

    flattenNavData(navData);
    // Remove duplicates based on ID
    const uniqueNavItems = allNavItems.filter((item, index, self) => index === self.findIndex((t) => t.id === item.id));
    return uniqueNavItems;
  }

  private readonly EXCLUDED_REDLINE_FIELDS: (string | RegExp)[] = [
    '#/invoiceId',
    '#/expenseInformation/expenseInfoMaxId',
    '#/webAddresses/webAddressMaxId',
    // Address verification metadata -- internal tracking fields, not user-visible
    // changes. Matched by regex so they are excluded wherever an address appears.
    /\/addressVerificationStatus$/,
    /\/poBoxIndicator$/,
    /\/addressType$/,
  ];

  /**
   * Recursively removes insert diffs with null target from the nested diff tree.
   * These are schema hydration artifacts, not real user changes.
   */
  private removeNullInsertDiffs(obj: any): void {
    if (!obj || typeof obj !== 'object') return;

    for (const key of Object.keys(obj)) {
      const val = obj[key];
      if (val?.decafDiff?.op === 'insert' && val.decafDiff.target == null) {
        delete obj[key];
      } else if (typeof val === 'object') {
        this.removeNullInsertDiffs(val);
      }
    }
  }

  /**
   * Sorts redlineData.model expenseInfoItems and reorders diff expenseInfoItems
   * to match, so the redline view displays in the same sorted order.
   */
  private sortRedlineExpenseInfo(diff: any, redlineData: any): void {
    const model = redlineData?.model;
    const modelItems = model?.expenseInformation?.expenseInfoItems;
    const diffItems = diff?.expenseInformation?.expenseInfoItems;
    if (!Array.isArray(modelItems) || !Array.isArray(diffItems)) return;
    if (modelItems.length !== diffItems.length) return;

    const comparator = (a, b) => {
      const aName = (a.model?.individualName || '').toLowerCase();
      const bName = (b.model?.individualName || '').toLowerCase();
      return aName.localeCompare(bName);
    };

    const usedDiffIndices = new Set<number>();
    const pairs = modelItems.map((m) => {
      const di = diffItems.findIndex((d, i) => {
        if (usedDiffIndices.has(i)) return false;
        const src = d?.decafDiff?.source;
        const tgt = d?.decafDiff?.target;
        if (src && typeof src === 'object') return m.expenseInfoItemId === src.expenseInfoItemId;
        if (tgt && typeof tgt === 'object') return m.expenseInfoItemId === tgt.expenseInfoItemId;
        const dId = d?.expenseInfoItemId?.decafDiff
          ? d.expenseInfoItemId.decafDiff.target ?? d.expenseInfoItemId.decafDiff.source
          : d?.expenseInfoItemId;
        return m.expenseInfoItemId === dId;
      });
      if (di !== -1) usedDiffIndices.add(di);
      return {
        model: m,
        diff: di !== -1 ? diffItems[di] : diffItems[modelItems.indexOf(m)],
      };
    });

    pairs.sort(comparator);

    model.expenseInformation.expenseInfoItems = pairs.map((p) => p.model);
    diff.expenseInformation.expenseInfoItems = pairs.map((p) => p.diff);
  }

  classifyDiffData(instanceData, navData, sourceVersion?) {
    const { diff, flattenedDiff } = deepClone(instanceData.redlineData);
    const flattenedDiffFiltered = flattenedDiff;

    // Support both exact JSON-pointer keys and RegExp patterns (AV metadata).
    Object.keys(flattenedDiffFiltered).forEach((key) => {
      const excluded = this.EXCLUDED_REDLINE_FIELDS.some((entry) =>
        entry instanceof RegExp ? entry.test(key) : entry === key,
      );
      if (excluded) delete flattenedDiffFiltered[key];
    });

    //Below is default logic from abstract other than using a filtered FlattenedDiff.
    const cacheKey = `${instanceData.instance.id}:${instanceData.instance.instanceVersion}${sourceVersion ? `:${sourceVersion}` : ''}`;
    if (this.cache[cacheKey]) {
      this.removeNullInsertDiffs(diff);
      this.sortRedlineExpenseInfo(diff, instanceData.redlineData);
      this.setCurrentRedlines(diff);
      return this.cache[cacheKey];
    }
    const dict = this.createflattenedDiffDictionary(flattenedDiffFiltered);
    if (Object.keys(dict).length === 0) {
      this.OS.showSnackbar({ text: 'No changes have been made to the filing yet', theme: 'warning' });
      return { open: false };
    }
    // Filter insert diffs where target is null.
    // This occurs when field is not in collectedData and gets hydrated as null.
    this.removeNullInsertDiffs(diff);
    this.sortRedlineExpenseInfo(diff, instanceData.redlineData);
    this.setCurrentRedlines(diff);
    const { schema, labels } = getDeepestRouteSnapshot(this.route).data.schemas;
    const pointers = Object.keys(dict);
    const flattenedDiffTrack = deepClone(dict);
    pointers
      .filter((pointer) => !labels[pointer])
      .forEach((pointer) => {
        const field = findField(
          schema,
          pointer
            .split('/')
            .slice(1)
            .filter((p) => !['200510', '200905'].includes(p)),
        );
        if (field?.title) {
          labels[pointer] = field.title
            .replace(/<br\s*\/?>/gi, ' ')
            .replace(/<[^>]*>/g, '')
            .trim();
        }
      });

    const customLabels = {};

    pointers.forEach((pointer) => {
      if (customLabels[pointer]) {
        labels[pointer] = customLabels[pointer];
      }
    });
    const result = {
      type: 'redline',
      title: this.currentTitle, // Use dynamic title
      open: true,
      errors: true,
      chips: [
        {
          text: 'Added',
          theme: 'green',
          type: 'insert',
          selected: true,
          get count() {
            return (
              result.sections
                .find((section) => section.id === 'all')
                ?.items.filter((item) => item.type === this.type)
                .reduce((acc, curr) => acc + curr.count ?? 1, 0) ?? 0
            );
          },
        },
        {
          text: 'Changed',
          theme: 'blue',
          type: 'update',
          selected: true,
          get count() {
            return (
              result.sections
                .find((section) => section.id === 'all')
                ?.items.filter((item) => item.type === this.type)
                .reduce((acc, curr) => acc + curr.count ?? 1, 0) ?? 0
            );
          },
        },
        {
          text: 'Deleted',
          theme: 'red',
          type: 'delete',
          selected: true,
          get count() {
            return (
              result.sections
                .find((section) => section.id === 'all')
                ?.items.filter((item) => item.type === this.type)
                .reduce((acc, curr) => acc + curr.count ?? 1, 0) ?? 0
            );
          },
        },
      ],
      get selectedTypes() {
        return this.chips.filter((chip) => chip.selected).map((chip) => chip.type);
      },
      get count() {
        return this.sections.find((section) => section.id === 'all')?.items?.filter((item) => !item.hide).length;
      },
    };
    result.sections = this.preprocessNavData(navData)
      .map(function (section) {
        return {
          id: section.id,
          title: section.text,
          items: pointers
            .filter((pointer) => {
              if (section.id === 'all') {
                return true;
              } else {
                return flattenedDiffTrack[pointer] && section.pointerList?.some((regex) => regex.test(pointer));
              }
            })
            .flatMap((pointer) => {
              if (section.id !== 'all') {
                flattenedDiffTrack[pointer] = undefined;
              }
              return ['insert', 'update', 'delete']
                .filter((type) => dict[pointer][type])
                .map((type) => ({
                  pointer,
                  type,
                  navData: section,
                  text: labels[pointer] ?? pointer,
                  count: dict[pointer][type]?.count ?? 1,
                  get hide() {
                    return !result.selectedTypes.includes(type);
                  },
                }));
            }),
          get count() {
            return this.items.filter((item) => !item.hide).reduce((acc, curr) => acc + curr.count ?? 1, 0);
          },
        };
      })
      .filter((section) => section.count);
    this.cache = { [cacheKey]: result };
    return result;
  }
}
